import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ActionId,
  HabitCategory,
  IsoDate,
  ObjectiveCategory,
  ObjectiveDraftPreviewInput,
  ObjectiveForecastView,
  ObjectiveId,
  ObjectiveLevelOfEffort,
  ObjectivePriority,
  ObjectiveReminder,
  ObjectiveSummaryView,
  ObjectiveView,
  WeekStart,
} from '@contracts';
import { computeDraftForecastPreview } from '@domain/forecast/objective-forecast';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, ChevronRight } from '@ui/shared/icons';
import { Field, inputStyle, PrimaryButton, SecondaryButton } from '@ui/shared/primitives/FormControls';
import { DatePickerField } from '@ui/shared/primitives/DatePickerField';
import { LifecycleConfirmDialog } from '@ui/shared/primitives/LifecycleFooter';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { localIsoDate } from '@ui/shared/format/date';
import {
  AutosaveStatusPill,
  CadenceStepper,
  CategoryPillSelect,
  EffortTileSelector,
  PrioritySegmentedControl,
  ReminderSchedulePicker,
  StatusSegmentedControl,
  type AutosaveState,
} from './FormPrimitives';
import { ActionsTable, type ActionRowData } from './ActionsTable';
import type { ActionDisplayStatus } from './ActionStatusPill';
import { RealismEnginePanel } from './RealismEnginePanel';
import { LinkedObjectivesSection } from './LinkedObjectivesSection';
import { LinkedHabitsSection } from './LinkedHabitsSection';
import { VisibilityTogglesSection } from './VisibilityTogglesSection';
import { PinnedToggleSection } from './PinnedToggleSection';

/**
 * Desktop-only Objective create/edit screen
 * (design/screens/objective-create-desktop.md, docs/specs/
 * phase-5-objective-creation-rebuild.md). A genuinely separate desktop
 * destination — same posture as the Phase 4 Dashboard — reached from
 * `ObjectivesScreen`'s "New objective" action and from an existing
 * objective's edit entry point, desktop viewport only. The phone
 * NewObjectiveScreen/EditObjectiveScreen trees are completely untouched.
 *
 * Local draft state models EVERY field this screen owns (title, why,
 * category, effort, target date, cadence goal, priority, reminder, actions)
 * so the live Realism Engine sidebar can recompute from in-memory state on
 * every keystroke via `computeDraftForecastPreview` — a pure, no-I/O domain
 * helper (ADR-0002 addendum 2026-07-04 D10) — BEFORE anything is persisted.
 * Once the objective exists (either an autosaved draft or a committed
 * objective), field edits also call the real DomainApi commands so nothing
 * is ever lost; the sidebar switches to the real `getObjective`-backed
 * forecast once `objectiveId` exists, per this screen's "committed math,
 * same shape" rule.
 */
export function ObjectiveFormScreenDesktop({
  objectiveId: initialObjectiveId,
  onDone,
  onDiscard,
  onOpenObjective,
  today: todayOverride,
}: {
  /** Null on the create path; an existing id on the edit path. */
  readonly objectiveId: string | null;
  readonly onDone: () => void;
  readonly onDiscard: () => void;
  readonly onOpenObjective?: (objectiveId: string) => void;
  /** Test override, mirroring TodayViewDesktop's own `today` prop — see that
   *  component's comment for the full "real clock vs fixture clock drift"
   *  story. Defaults to the real wall clock in production. */
  readonly today?: IsoDate;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const today = todayOverride ?? (localIsoDate() as IsoDate);

  const [objectiveId, setObjectiveId] = useState<string | null>(initialObjectiveId);
  const [objective, setObjective] = useState<ObjectiveView | null>(null);
  const [loading, setLoading] = useState(initialObjectiveId !== null);
  const [notFound, setNotFound] = useState(false);
  const [allObjectives, setAllObjectives] = useState<readonly ObjectiveSummaryView[]>([]);
  const [habitsById, setHabitsById] = useState<
    ReadonlyMap<string, { readonly category: HabitCategory; readonly habitKind: 'build' | 'avoid' }>
  >(new Map());

  // -- Draft field state --------------------------------------------------
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ObjectiveCategory | null>(null);
  const [successMetric, setSuccessMetric] = useState('');
  const [levelOfEffort, setLevelOfEffort] = useState<ObjectiveLevelOfEffort | null>(null);
  const [targetDate, setTargetDate] = useState<IsoDate>(defaultTargetDate(today));
  const [cadenceGoal, setCadenceGoal] = useState<number | null>(null);
  const [priority, setPriority] = useState<ObjectivePriority>('medium');
  const [reminder, setReminder] = useState<ObjectiveReminder | null>(null);
  const [actions, setActions] = useState<readonly DraftAction[]>([]);
  const [includeInWeeklyReview, setIncludeInWeeklyReview] = useState(true);
  const [surfaceOnToday, setSurfaceOnToday] = useState(true);
  const [ambientRewardOnComplete, setAmbientRewardOnComplete] = useState(true);
  const [linked, setLinked] = useState<ReadonlyArray<{ readonly objectiveId: string; readonly title: string }>>([]);

  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [receipt, setReceipt] = useState<string | null>(null);
  const [justCompletedId, setJustCompletedId] = useState<string | null>(null);

  const isEditPath = objectiveId !== null;
  const isCommitted = objective !== null && objective.state !== 'draft';

  // Ticks the autosave "Ns ago" text every 30s, matching Dashboard's
  // freshnessText cadence (no per-second jitter).
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    void api.listObjectives({ includeArchived: false }).then(setAllObjectives);
  }, [api, version]);

  // Objective-to-objective prev/next switcher's habit-linking counterpart:
  // ObjectiveDetailScreen.tsx's own `habitsById` join, brought over so the
  // "Linked habits" card here shows the same category icon + adherence text
  // (display-layer join, no new domain math — see LinkedHabitsSection.tsx).
  useEffect(() => {
    void api.listHabits().then((habits) => {
      setHabitsById(new Map(habits.map((h) => [h.habitId, { category: h.category, habitKind: h.plan.habitKind }])));
    });
  }, [api, version]);

  // Prev/next objective switcher (preserves ObjectiveDetailScreen's "OBJ N / M"
  // navigation affordance now that viewing and editing converge into this one
  // screen at desktop — owner direct correction: "it can't feel different
  // ux"). Reuses the already-fetched `allObjectives` list rather than a
  // second listObjectives() call with different filters.
  const position = useMemo(() => {
    const index = allObjectives.findIndex((o) => o.objectiveId === objectiveId);
    return { index, count: allObjectives.length };
  }, [allObjectives, objectiveId]);

  const goToOffset = useCallback(
    (offset: number) => {
      if (position.index < 0) return;
      const targetIndex = position.index + offset;
      if (targetIndex < 0 || targetIndex >= allObjectives.length) return;
      const target = allObjectives[targetIndex];
      if (target) onOpenObjective?.(target.objectiveId);
    },
    [position, allObjectives, onOpenObjective],
  );

  // Load an existing objective (edit path) and hydrate every draft field
  // from it. Runs once per objectiveId/version, never clobbers mid-typing
  // local state after the initial hydration (mirrors EditObjectiveScreen's
  // own "only re-fetch on id/version change" rule) — this screen re-hydrates
  // on every domain version change since other edits (e.g. from a link
  // target) should be reflected, but per-keystroke local edits win against
  // hydration because commands here are called immediately on each change,
  // so the domain state always agrees with local state by the next version.
  useEffect(() => {
    if (initialObjectiveId === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api
      .getObjective(initialObjectiveId as ObjectiveId)
      .then((obj) => {
        if (cancelled) return;
        setObjective(obj);
        setTitle(obj.title);
        setDescription(obj.why);
        setCategory(obj.category);
        setSuccessMetric('');
        setLevelOfEffort(obj.levelOfEffort);
        setTargetDate(obj.targetDate);
        setCadenceGoal(obj.cadenceGoalPerWeek);
        setPriority(obj.priority ?? 'medium');
        setReminder(obj.reminder);
        setActions(
          obj.actions.map((a) => ({
            actionId: a.actionId,
            title: a.title,
            dueDate: a.dueDate,
            status: a.status,
            effortMinutes: a.effortMinutes,
            effortLevel: a.effortMinutes ? effortMinutesToLevel(a.effortMinutes) : null,
          })),
        );
        setIncludeInWeeklyReview(obj.includeInWeeklyReview);
        setSurfaceOnToday(obj.surfaceOnToday);
        setAmbientRewardOnComplete(obj.ambientRewardOnComplete);
        setLinked(obj.linkedObjectives);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, initialObjectiveId, version]);

  const markDirtySaving = useCallback(() => {
    setDirty(true);
    setSaveState('saving');
  }, []);

  const markSaved = useCallback(() => {
    setSaveState('saved');
    setLastSavedAt(Date.now());
  }, []);

  // -- Draft-vs-committed forecast source ----------------------------------
  // Draft (not yet persisted at all): compute a live preview from in-memory
  // field values via the pure, no-I/O computeDraftForecastPreview (ADR-0002
  // addendum D10) — no event written, recomputes on every render.
  // Committed (objectiveId exists, whether still 'draft' status or 'active'):
  // use the real getObjective-backed forecast on `objective`.
  const forecast: ObjectiveForecastView = useMemo(() => {
    if (objective) return objective.forecast;
    const input: ObjectiveDraftPreviewInput = {
      targetDate,
      cadenceGoalPerWeek: cadenceGoal,
      actionCount: actions.length,
    };
    return computeDraftForecastPreview(input, today);
  }, [objective, targetDate, cadenceGoal, actions.length, today]);

  const progress = useMemo(() => {
    const done = actions.filter((a) => a.status === 'done').length;
    return { done, total: actions.length };
  }, [actions]);

  // -- Persistence helpers --------------------------------------------------

  /** Ensures the objective exists in the domain before any per-field command
   *  can target it — the FIRST autosave creates it (as a draft, per this
   *  screen's ObjectiveDraft.initialStatus: 'draft' contract), every
   *  subsequent field edit calls the real per-field commands directly.
   *
   *  BUG FIX (found via this screen's own Objectives-catalog category
   *  filter test): `createObjective`'s event schema (`ObjectiveCreatedEvent`)
   *  only ever carries title/why/targetDate/actions/initialStatus —
   *  category/levelOfEffort/priority/reminder/cadenceGoalPerWeek/toggles all
   *  ride on a SEPARATE `objective.edited` event via `updateObjectiveDetails`
   *  (commands.ts's own doc comment: "kept as its own command"). Passing
   *  those fields into `createObjective` (as this used to do) was silently
   *  a no-op — the command has no field for them and drops them — so any
   *  category/effort/priority/reminder/cadence/toggle chosen during the
   *  initial typing phase (before the first autosave fires) was lost
   *  forever unless the user happened to re-touch that field afterward
   *  (when objectiveId is already set and the per-field handlers call
   *  updateObjectiveDetails directly). Fixed by following creation with the
   *  same updateObjectiveDetails call every later in-place edit already
   *  uses. */
  const ensureCreated = useCallback(async (): Promise<string> => {
    if (objectiveId) return objectiveId;
    const newId = await api.createObjective({
      title: title.trim() || 'Untitled objective',
      why: description,
      targetDate,
      actions: actions.map((a) => ({
        title: a.title,
        dueDate: a.dueDate as IsoDate | null,
        milestoneTag: null,
        effortMinutes: a.effortMinutes,
      })),
      initialStatus: 'draft',
    });
    await api.updateObjectiveDetails(newId, {
      category,
      levelOfEffort,
      priority,
      reminder,
      cadenceGoalPerWeek: cadenceGoal,
      includeInWeeklyReview,
      surfaceOnToday,
      ambientRewardOnComplete,
    });
    setObjectiveId(newId);
    const created = await api.getObjective(newId);
    setObjective(created);
    setActions(
      created.actions.map((a) => ({
        actionId: a.actionId,
        title: a.title,
        dueDate: a.dueDate,
        status: a.status,
        effortMinutes: a.effortMinutes,
        effortLevel: a.effortMinutes ? effortMinutesToLevel(a.effortMinutes) : null,
      })),
    );
    return newId;
  }, [
    objectiveId,
    api,
    title,
    description,
    targetDate,
    actions,
    category,
    levelOfEffort,
    priority,
    reminder,
    cadenceGoal,
    includeInWeeklyReview,
    surfaceOnToday,
    ambientRewardOnComplete,
  ]);

  const reload = useCallback(async () => {
    if (!objectiveId) return;
    const fresh = await api.getObjective(objectiveId as ObjectiveId);
    setObjective(fresh);
    setLinked(fresh.linkedObjectives);
  }, [api, objectiveId]);

  // Debounced autosave: field edits create the draft (if not created yet)
  // roughly 2s after the last edit, matching SS8's "autosave cadence" note.
  // Only relevant pre-creation; once created, per-field commands persist
  // immediately (no debounce needed, matching the rest of the product's
  // "no save buttons" rule).
  const autosaveTimer = useRef<number | null>(null);
  const scheduleAutosave = useCallback(() => {
    markDirtySaving();
    if (objectiveId) {
      // Already created: nothing to debounce here, the field's own command
      // already persisted synchronously by the time this is called.
      markSaved();
      return;
    }
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      void ensureCreated().then(() => markSaved());
    }, 2000);
  }, [objectiveId, ensureCreated, markDirtySaving, markSaved]);

  useEffect(() => {
    return () => {
      if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    };
  }, []);

  // -- Field change handlers -------------------------------------------------
  // Every handler updates local state immediately (so the sidebar recomputes
  // instantly) and, once the objective is created, calls the matching real
  // command so nothing is UI-only state.

  function handleTitleChange(next: string) {
    setTitle(next);
    if (objectiveId) void api.editObjective(objectiveId as ObjectiveId, { title: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleDescriptionChange(next: string) {
    setDescription(next);
    if (objectiveId) void api.editObjective(objectiveId as ObjectiveId, { why: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleCategoryChange(next: ObjectiveCategory) {
    setCategory(next);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, { category: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleSuccessMetricChange(next: string) {
    setSuccessMetric(next);
    scheduleAutosave();
  }
  function handleEffortChange(next: ObjectiveLevelOfEffort) {
    setLevelOfEffort(next);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, { levelOfEffort: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleTargetDateChange(next: string) {
    setTargetDate(next as IsoDate);
    if (objectiveId) void api.adjustObjectiveTargetDateManually(objectiveId as ObjectiveId, next as IsoDate).then(markSaved);
    else scheduleAutosave();
  }
  function handleCadenceChange(next: number | null) {
    setCadenceGoal(next);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, { cadenceGoalPerWeek: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handlePriorityChange(next: ObjectivePriority) {
    setPriority(next);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, { priority: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleReminderChange(next: ObjectiveReminder | null) {
    setReminder(next);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, { reminder: next }).then(markSaved);
    else scheduleAutosave();
  }
  function handleStatusChange(next: 'active' | 'on_hold') {
    if (!objectiveId) return;
    void api.setObjectiveStatus(objectiveId as ObjectiveId, next).then(reload).then(markSaved);
  }
  function handleTogglesChange(changes: {
    readonly includeInWeeklyReview?: boolean;
    readonly surfaceOnToday?: boolean;
    readonly ambientRewardOnComplete?: boolean;
  }) {
    if (changes.includeInWeeklyReview !== undefined) setIncludeInWeeklyReview(changes.includeInWeeklyReview);
    if (changes.surfaceOnToday !== undefined) setSurfaceOnToday(changes.surfaceOnToday);
    if (changes.ambientRewardOnComplete !== undefined) setAmbientRewardOnComplete(changes.ambientRewardOnComplete);
    if (objectiveId) void api.updateObjectiveDetails(objectiveId as ObjectiveId, changes).then(markSaved);
    else scheduleAutosave();
  }

  // R8.8/AC-T19/AC-T20: pin/unpin only applies to a committed objective
  // (objectiveId always exists wherever this is called from — the sidebar
  // section itself is gated on isEditPath). No math consequence; a plain
  // reload refreshes DesktopShell's own separate listPinnedObjectives read
  // on its next domain-version tick. Mirrors handleLink/handleUnlink's own
  // shape (no markSaved — pinning isn't part of the autosave-status story).
  async function handlePinned(next: boolean) {
    if (!objectiveId) return;
    await api.setObjectivePinned(objectiveId as ObjectiveId, next, null);
    await reload();
  }

  // -- Actions table handlers -------------------------------------------------

  async function handleAddAction(actionTitle: string) {
    if (!objectiveId) {
      setActions((prev) => [
        ...prev,
        { actionId: `local-${prev.length}-${actionTitle}`, title: actionTitle, dueDate: null, status: 'to_do', effortMinutes: null, effortLevel: null },
      ]);
      scheduleAutosave();
      return;
    }
    const newId = await api.addAction(objectiveId as ObjectiveId, { title: actionTitle, dueDate: null, milestoneTag: null, position: null });
    setActions((prev) => [...prev, { actionId: newId, title: actionTitle, dueDate: null, status: 'to_do', effortMinutes: null, effortLevel: null }]);
    markSaved();
  }

  async function handleRenameAction(actionId: string, nextTitle: string) {
    setActions((prev) => prev.map((a) => (a.actionId === actionId ? { ...a, title: nextTitle } : a)));
    if (objectiveId) await api.editAction(objectiveId as ObjectiveId, actionId as ActionId, { title: nextTitle });
    markSaved();
  }

  async function handleSetDueDate(actionId: string, dueDate: string | null) {
    setActions((prev) => prev.map((a) => (a.actionId === actionId ? { ...a, dueDate } : a)));
    if (objectiveId) await api.editAction(objectiveId as ObjectiveId, actionId as ActionId, { dueDate: dueDate as IsoDate | null });
    markSaved();
  }

  async function handleSetEffort(actionId: string, effortLevel: number | null, effortMinutes: number | null) {
    setActions((prev) => prev.map((a) => (a.actionId === actionId ? { ...a, effortLevel, effortMinutes } : a)));
    if (objectiveId) await api.editAction(objectiveId as ObjectiveId, actionId as ActionId, { effortMinutes });
    markSaved();
  }

  async function handleSetActionStatus(actionId: string, status: ActionDisplayStatus) {
    const current = actions.find((a) => a.actionId === actionId);
    if (!current) return;

    if (status === 'done') {
      if (objectiveId) {
        await api.completeAction(objectiveId as ObjectiveId, actionId as ActionId, today);
        setJustCompletedId(actionId);
        window.setTimeout(() => setJustCompletedId(null), 700);
        await reload();
      } else {
        setActions((prev) => prev.map((a) => (a.actionId === actionId ? { ...a, status: 'done' } : a)));
      }
      return;
    }

    if (current.status === 'done') {
      // Reverting a done action back to to_do/doing goes through revert
      // (entryEventId), matching the phone screen's own toggle pattern.
      const real = objective?.actions.find((a) => a.actionId === actionId);
      if (real?.entryEventId && objectiveId) {
        await api.revertLogEntry(real.entryEventId);
        await reload();
      }
      return;
    }

    setActions((prev) => prev.map((a) => (a.actionId === actionId ? { ...a, status } : a)));
    if (objectiveId) {
      await api.setActionStatus(objectiveId as ObjectiveId, actionId as ActionId, status);
    }
    markSaved();
  }

  async function handleRemoveAction(actionId: string) {
    setActions((prev) => prev.filter((a) => a.actionId !== actionId));
    if (objectiveId) await api.removeAction(objectiveId as ObjectiveId, actionId as ActionId);
    markSaved();
  }

  async function handleReorder(orderedActionIds: readonly string[]) {
    setActions((prev) => {
      const byId = new Map(prev.map((a) => [a.actionId, a]));
      return orderedActionIds.map((id) => byId.get(id)).filter((a): a is DraftAction => a !== undefined);
    });
    if (objectiveId) await api.reorderActions(objectiveId as ObjectiveId, orderedActionIds as readonly ActionId[]);
    markSaved();
  }

  // -- Linking ----------------------------------------------------------------

  async function handleLink(targetId: string) {
    if (!objectiveId) return;
    await api.linkObjectives(objectiveId as ObjectiveId, targetId as ObjectiveId);
    await reload();
  }

  async function handleUnlink(targetId: string) {
    if (!objectiveId) return;
    await api.unlinkObjectives(objectiveId as ObjectiveId, targetId as ObjectiveId);
    await reload();
  }

  // -- Top bar actions ----------------------------------------------------------

  async function handleCommit() {
    const id = await ensureCreated();
    await api.setObjectiveStatus(id as ObjectiveId, 'active');
    await reload();
    setReceipt('Committed — now active and visible on Today.');
    window.setTimeout(() => setReceipt(null), 2000);
  }

  async function handleSaveDraft() {
    await ensureCreated();
    markSaved();
  }

  function handleDiscardRequest() {
    if (!dirty && title.trim().length === 0 && actions.length === 0) {
      onDiscard();
      return;
    }
    setDiscardConfirmOpen(true);
  }

  async function handleDiscardConfirm() {
    if (objectiveId) {
      await api.setObjectiveStatus(objectiveId as ObjectiveId, 'archived');
    }
    onDiscard();
  }

  async function handleApplySuggestion() {
    if (!forecast.suggestion || !objectiveId) return;
    await api.acceptObjectiveDateSuggestion({
      objectiveId: objectiveId as ObjectiveId,
      currentTargetDate: targetDate,
      suggestedTargetDate: forecast.suggestion.suggestedTargetDate,
      requiredPaceActionsPerWeek: forecast.suggestion.requiredPaceActionsPerWeek,
      reviewWeek: currentWeekStart(),
    });
    setTargetDate(forecast.suggestion.suggestedTargetDate);
    await reload();
  }

  const canCommit = title.trim().length > 0 && actions.length > 0;

  const autosaveState: AutosaveState = receipt
    ? { kind: 'receipt', message: receipt }
    : isCommitted
      ? { kind: 'active_saved', secondsAgo: lastSavedAt ? Math.floor((nowTick - lastSavedAt) / 1000) : null }
      : saveState === 'saving'
        ? { kind: 'draft_unsaved' }
        : { kind: 'draft_saved', secondsAgo: lastSavedAt ? Math.floor((nowTick - lastSavedAt) / 1000) : 0 };

  const linkCandidates = allObjectives.filter(
    (o) => o.objectiveId !== objectiveId && !linked.some((l) => l.objectiveId === o.objectiveId),
  );

  const actionRows: readonly ActionRowData[] = actions.map((a) => ({
    actionId: a.actionId,
    title: a.title,
    dueDate: a.dueDate,
    status: a.status,
    effortLevel: a.effortLevel,
    effortMinutes: a.effortMinutes,
    justCompleted: a.actionId === justCompletedId,
  }));

  if (loading) {
    return (
      <main aria-label="Objective form" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
        <SkeletonBlock height={20} width={160} />
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBlock height={120} />
            <SkeletonBlock height={200} />
            <SkeletonBlock height={240} />
          </div>
          <SkeletonBlock height={480} />
        </div>
      </main>
    );
  }

  if (notFound) {
    return (
      <main aria-label="Objective form" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) 0', maxWidth: '1600px', margin: '0 auto' }}>
        <Breadcrumb title={null} />
        <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>Objective not found.</p>
      </main>
    );
  }

  return (
    <main
      aria-label={isEditPath ? `Edit objective: ${title}` : 'New objective'}
      style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}
    >
      <TopBar
        title={isEditPath ? title : null}
        autosaveState={autosaveState}
        canCommit={canCommit && !isCommitted}
        isCommitted={isCommitted}
        switcher={isEditPath && position.index >= 0 ? position : null}
        onSwitcherStepBack={() => goToOffset(-1)}
        onSwitcherStepForward={() => goToOffset(1)}
        onNavigateBack={onDone}
        onDiscard={handleDiscardRequest}
        onSaveDraft={() => void handleSaveDraft()}
        onCommit={() => void handleCommit()}
      />

      {discardConfirmOpen && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <LifecycleConfirmDialog
            ariaLabel="Discard this draft"
            message="Discard this draft? Nothing has been committed — this removes what you've entered so far."
            confirmLabel="Discard"
            onConfirm={() => {
              setDiscardConfirmOpen(false);
              void handleDiscardConfirm();
            }}
            onCancel={() => setDiscardConfirmOpen(false)}
          />
        </div>
      )}

      <div style={{ marginTop: 'var(--space-5)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <SectionHeader>01 · Definition</SectionHeader>
          <Field label="Title">
            <input value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="e.g. Finish the AWS Solutions Architect path" style={inputStyle} />
          </Field>
          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="What does finishing this actually look like? A sentence or two is plenty."
              rows={3}
              style={{ ...inputStyle, height: 'auto', minHeight: '76px', padding: 'var(--space-3)', resize: 'vertical' }}
            />
          </Field>
          <Field label="Category">
            <CategoryPillSelect value={category} onChange={handleCategoryChange} />
          </Field>
          <Field label="Success metric">
            <input
              value={successMetric}
              onChange={(e) => handleSuccessMetricChange(e.target.value)}
              placeholder="How will you know this is actually done? e.g. 'AWS cert in hand' or 'Passport stamped 3 times'"
              style={inputStyle}
            />
          </Field>

          <SectionHeader>02 · Shape</SectionHeader>
          <Field label="Level of effort">
            <EffortTileSelector value={levelOfEffort} onChange={handleEffortChange} />
          </Field>
          <Field label="Target date" as="div">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ flex: 1 }}>
                <DatePickerField value={targetDate} today={today} isDefaultValue={false} onChange={handleTargetDateChange} />
              </div>
              <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '2px 10px', borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap' }}>
                {weeksOut(today, targetDate)} weeks out
              </span>
            </div>
          </Field>
          <Field label="Cadence goal">
            <CadenceStepper value={cadenceGoal} onChange={handleCadenceChange} />
          </Field>
          <Field label="Priority">
            <PrioritySegmentedControl value={priority} onChange={handlePriorityChange} />
          </Field>
          {isEditPath && isCommitted && objective && objective.state !== 'archived' && (
            <Field label="Status">
              <StatusSegmentedControl
                value={objective.state === 'on_hold' ? 'on_hold' : 'active'}
                onChange={handleStatusChange}
              />
            </Field>
          )}
          <Field label="Reminder">
            <ReminderSchedulePicker value={reminder} onChange={handleReminderChange} />
          </Field>

          <SectionHeader>03 · Actions</SectionHeader>
          <ActionsTable
            today={today}
            rows={actionRows}
            onReorder={(ids) => void handleReorder(ids)}
            onRename={(id, t) => void handleRenameAction(id, t)}
            onSetDueDate={(id, d) => void handleSetDueDate(id, d)}
            onSetEffort={(id, level, minutes) => void handleSetEffort(id, level, minutes)}
            onSetStatus={(id, status) => void handleSetActionStatus(id, status)}
            onRemove={(id) => void handleRemoveAction(id)}
            onAdd={(t) => void handleAddAction(t)}
          />
        </div>

        <RealismEnginePanel
          forecast={forecast}
          progress={progress}
          targetDate={targetDate}
          onApplySuggestion={() => void handleApplySuggestion()}
          onDismissSuggestion={() => setSuggestionDismissed(true)}
          suggestionDismissed={suggestionDismissed}
        >
          <LinkedObjectivesSection
            linked={linked}
            candidates={linkCandidates}
            onLink={(id) => void handleLink(id)}
            onUnlink={(id) => void handleUnlink(id)}
            onOpenObjective={onOpenObjective}
          />
          <LinkedHabitsSection linkedHabits={objective?.linkedHabits ?? []} habitsById={habitsById} />
          {isEditPath && (
            <PinnedToggleSection pinned={objective?.pinned ?? false} onChange={(next) => void handlePinned(next)} />
          )}
          <VisibilityTogglesSection
            includeInWeeklyReview={includeInWeeklyReview}
            surfaceOnToday={surfaceOnToday}
            ambientRewardOnComplete={ambientRewardOnComplete}
            onChange={handleTogglesChange}
          />
        </RealismEnginePanel>
      </div>

    </main>
  );
}

interface DraftAction {
  readonly actionId: string;
  readonly title: string;
  readonly dueDate: string | null;
  readonly status: 'to_do' | 'doing' | 'done';
  readonly effortMinutes: number | null;
  readonly effortLevel: number | null;
}

function effortMinutesToLevel(minutes: number): number {
  if (minutes <= 20) return 1;
  if (minutes <= 60) return 2;
  return 3;
}

function defaultTargetDate(today: string): IsoDate {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + 30);
  return localIsoDate(d) as IsoDate;
}

function weeksOut(today: string, targetDate: string): number {
  const days = Math.round((Date.parse(targetDate) - Date.parse(today)) / 86_400_000);
  return Math.max(0, Math.round(days / 7));
}

/**
 * DOMAIN-API GAP (same one flagged in ObjectiveDetailScreen.tsx and
 * DashboardScreen.desktop.tsx): duplicates Monday-of-week math locally
 * rather than a real `getCurrentWeekStart()` domain addition — kept
 * consistent with those two existing local placeholders rather than
 * inventing a third, different one.
 */
function currentWeekStart(): WeekStart {
  const now = new Date();
  const day = now.getDay();
  const diff = (day + 6) % 7;
  now.setDate(now.getDate() - diff);
  return localIsoDate(now) as WeekStart;
}

function SectionHeader({ children }: { readonly children: string }) {
  return (
    <h2
      style={{
        marginTop: 'var(--space-6)',
        fontSize: 'var(--type-label-size)',
        fontWeight: 'var(--type-label-weight)',
        letterSpacing: 'var(--type-label-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </h2>
  );
}

function Breadcrumb({ title, onNavigateBack }: { readonly title: string | null; readonly onNavigateBack?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--type-caption-size)', fontWeight: 600 }}>
      {onNavigateBack ? (
        <button type="button" onClick={onNavigateBack} style={{ color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}>
          Objectives
        </button>
      ) : (
        <span style={{ color: 'var(--text-muted)' }}>Objectives</span>
      )}
      <ChevronRight size={14} color="var(--text-muted)" aria-hidden="true" />
      <span style={{ color: 'var(--text-primary)' }}>{title ? truncate(title, 32) : 'New'}</span>
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function TopBar({
  title,
  autosaveState,
  canCommit,
  isCommitted,
  switcher,
  onSwitcherStepBack,
  onSwitcherStepForward,
  onNavigateBack,
  onDiscard,
  onSaveDraft,
  onCommit,
}: {
  readonly title: string | null;
  readonly autosaveState: AutosaveState;
  readonly canCommit: boolean;
  readonly isCommitted: boolean;
  /** Prev/next objective position, or null when there's no switcher to show (create path, or the objective isn't in the fetched list). */
  readonly switcher: { readonly index: number; readonly count: number } | null;
  readonly onSwitcherStepBack: () => void;
  readonly onSwitcherStepForward: () => void;
  readonly onNavigateBack?: () => void;
  readonly onDiscard: () => void;
  readonly onSaveDraft: () => void;
  readonly onCommit: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <Breadcrumb title={title} onNavigateBack={onNavigateBack} />
        <AutosaveStatusPill state={autosaveState} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        {switcher && (
          <ObjectiveSwitcher position={switcher} onStepBack={onSwitcherStepBack} onStepForward={onSwitcherStepForward} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={onDiscard}
            style={{ minHeight: 'var(--touch-target-min)', padding: '0 var(--space-3)', color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)', fontWeight: 600 }}
          >
            Discard
          </button>
          <SecondaryButton onClick={onSaveDraft}>Save draft</SecondaryButton>
          {!isCommitted && (
            <PrimaryButton onClick={onCommit} disabled={!canCommit}>
              Commit objective
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Prev/next objective switcher (mirrors ObjectiveDetailScreen's
 * ObjHeaderSwitcher position readout — "no wrap" at either end, matching
 * that screen's SS1.1 rule) — brought over so this converged desktop screen
 * doesn't lose the one navigation affordance the old detail screen offered
 * that this form screen didn't previously have.
 */
function ObjectiveSwitcher({
  position,
  onStepBack,
  onStepForward,
}: {
  readonly position: { readonly index: number; readonly count: number };
  readonly onStepBack: () => void;
  readonly onStepForward: () => void;
}) {
  const atStart = position.index <= 0;
  const atEnd = position.index >= position.count - 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
      <button
        type="button"
        onClick={onStepBack}
        disabled={atStart}
        aria-disabled={atStart}
        aria-label="Previous objective"
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: atStart ? 0.4 : 1,
        }}
      >
        <ChevronLeft size={16} color="var(--text-muted)" aria-hidden="true" />
      </button>
      <span
        className="num"
        style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, letterSpacing: '0.1em', color: 'var(--text-muted)' }}
      >
        {`OBJ ${position.index + 1} / ${position.count}`}
      </span>
      <button
        type="button"
        onClick={onStepForward}
        disabled={atEnd}
        aria-disabled={atEnd}
        aria-label="Next objective"
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: atEnd ? 0.4 : 1,
        }}
      >
        <ChevronRight size={16} color="var(--text-muted)" aria-hidden="true" />
      </button>
    </div>
  );
}
